import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const api = axios.create({ baseURL: API, withCredentials: true });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("rb_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export const TIER_META = {
  mild: { label: "Mild", color: "#10b981", desc: "A gentle ribbing." },
  medium: { label: "Medium", color: "#f59e0b", desc: "Turn up the heat." },
  savage: { label: "Savage", color: "#ef4444", desc: "No holding back." },
  no_mercy: { label: "No Mercy", color: "#f97316", desc: "Absolute devastation." },
};

export function roasterFlair(wins) {
  if (wins >= 25) return "LEGEND ROASTER";
  if (wins >= 10) return "TOP ROASTER";
  if (wins >= 3) return "RISING ROASTER";
  return null;
}

export function flairKey(wins) {
  if (wins >= 25) return "legend";
  if (wins >= 10) return "top";
  if (wins >= 3) return "rising";
  return null;
}

export const MILESTONES = {
  rising: { label: "Rising Roaster", threshold: 3, blurb: "3 roasts picked. People are starting to fear your keyboard." },
  top: { label: "Top Roaster", threshold: 10, blurb: "10 wins. You're officially one of the sharpest tongues here." },
  legend: { label: "Legend Roaster", threshold: 25, blurb: "25 wins. Certified menace. A living cautionary tale." },
};

const AVATAR_SVG = {
  flame: "<svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24'><path d='M12 2c1 4 5 5 5 9a5 5 0 1 1-10 0c0-2 1-3.2 2-4.2 0 2 1 3.2 2 3.2 0-3-1-4-1-8z' fill='white'/></svg>",
  skull: "<svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24'><circle cx='12' cy='10' r='7.5' fill='white'/><circle cx='9' cy='10' r='1.7' fill='#0b0b0b'/><circle cx='15' cy='10' r='1.7' fill='#0b0b0b'/><rect x='9' y='16.5' width='6' height='3.5' rx='1.2' fill='white'/></svg>",
  bolt: "<svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24'><path d='M13 2 4 14h6l-1 8 9-12h-6z' fill='white'/></svg>",
  star: "<svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24'><path d='M12 2l3 6 7 1-5 4.9 1.2 7L12 17.8 5.8 20.9 7 13.9 2 9l7-1z' fill='white'/></svg>",
  devil: "<svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24'><path d='M5 3l4 4-4 1zM19 3l-4 4 4 1z' fill='white'/><circle cx='12' cy='13' r='7.5' fill='white'/><circle cx='9.4' cy='12' r='1.5' fill='#0b0b0b'/><circle cx='14.6' cy='12' r='1.5' fill='#0b0b0b'/></svg>",
  ghost: "<svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24'><path d='M5 12a7 7 0 0 1 14 0v9l-2.3-2-2.3 2-2.4-2-2.3 2L5 21z' fill='white'/><circle cx='9.5' cy='11' r='1.3' fill='#0b0b0b'/><circle cx='14.5' cy='11' r='1.3' fill='#0b0b0b'/></svg>",
  crown: "<svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24'><path d='M3 8l3.5 4.5L12 5l5.5 7.5L21 8v11H3z' fill='white'/></svg>",
  bomb: "<svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24'><circle cx='11' cy='14.5' r='7' fill='white'/><path d='M16 8.5l3-3M19.5 5.5l.8 1.8M19.5 5.5l-1.8-.8' stroke='white' stroke-width='2' fill='none' stroke-linecap='round'/></svg>",
};

// Ordered by unlock tier. `unlock` = accepted-roast wins required to select it.
export const AVATARS = [
  { id: "flame", bg: "#ef4444", unlock: 0 },
  { id: "bolt", bg: "#f59e0b", unlock: 0 },
  { id: "star", bg: "#3b82f6", unlock: 0 },
  { id: "skull", bg: "#a855f7", unlock: 3 },
  { id: "ghost", bg: "#14b8a6", unlock: 3 },
  { id: "devil", bg: "#dc2626", unlock: 10 },
  { id: "bomb", bg: "#6366f1", unlock: 10 },
  { id: "crown", bg: "#eab308", unlock: 25 },
];

export const avatarSvgUri = (id) => (AVATAR_SVG[id] ? "data:image/svg+xml," + encodeURIComponent(AVATAR_SVG[id]) : null);
export const avatarBg = (id) => (AVATARS.find((a) => a.id === id) || {}).bg || "#3f3f46";
export const avatarUnlock = (id) => (AVATARS.find((a) => a.id === id) || {}).unlock || 0;

// The 5 named brand modes (Brand Architecture). Each carries an accent so the
// tonal split reads visually — VERDICT is cool/teal (professional feedback) vs
// ROAST's warm fire tones (savage/entertainment).
export const MODES = [
  { key: "roast", emoji: "🔥", label: "ROAST", to: "/app/submit", tid: "mode-roast",
    tag: "Paid", accent: "#ff4500", desc: "Pay a flat fee, get roasted by real humans." },
  { key: "verdict", emoji: "🎤", label: "VERDICT", to: "/app/submit", tid: "mode-verdict",
    tag: "Paid", accent: "#2dd4bf", desc: "High-stakes honest feedback — resume, pitch, logo." },
  { key: "roastoff", emoji: "⚔️", label: "ROAST-OFF", to: "/app/battles", tid: "mode-roastoff",
    tag: "Free", accent: "#a855f7", desc: "Challenge a friend head-to-head. The crowd decides." },
  { key: "arena", emoji: "🏆", label: "ARENA", to: "/app/arena", tid: "mode-arena",
    tag: "Free", accent: "#3b82f6", desc: "Leaderboards, quizzes and free social play." },
  { key: "hottake", emoji: "🌶️", label: "HOT TAKE", to: "/hot-takes", tid: "mode-hottake",
    tag: "Free", accent: "#a3e635", desc: "Drop an opinion. Watch the crowd agree — or not." },
];

// Survival Score (Sec 9) — cumulative, capped 100. Named tiers.
export const SURVIVAL_TIERS = [
  { hi: 20, label: "Fragile" }, { hi: 40, label: "Survivor" }, { hi: 60, label: "Taking It" },
  { hi: 80, label: "Iron Skin" }, { hi: 95, label: "Unroastable" }, { hi: 100, label: "Roast Legend" },
];
export function survivalLabel(score) {
  return (SURVIVAL_TIERS.find((t) => score <= t.hi) || SURVIVAL_TIERS[SURVIVAL_TIERS.length - 1]).label;
}


export const OCCASION_META = {
  birthday: { label: "Birthday", emoji: "🎂" },
  breakup: { label: "Breakup", emoji: "💔" },
  new_job: { label: "New Job", emoji: "💼" },
  just_because: { label: "Just Because", emoji: "🎁" },
  revenge: { label: "Revenge", emoji: "😈" },
  other: { label: "Other", emoji: "✨" },
};

export default api;
export { API };